import * as React from "react";
import { Check, ChevronDown, Search } from "lucide-react";
import { cn } from "../../lib/cn";
import { Popover, PopoverContent, PopoverTrigger } from "../Popover";
import { Skeleton } from "../Skeleton";

export interface ComboboxOption {
  readonly value: string;
  readonly label: string;
  /**
   * Muted secondary text shown next to the label (e.g. a username that tells
   * apart two people with the same name). Also matched against the search query.
   */
  readonly description?: string;
  /** Extra text matched against the search query but not shown (e.g. a code or id). */
  readonly keywords?: string;
}

const OPTION_ROW_WIDTHS = ["w-3/4", "w-1/2", "w-2/3"];

// Mirrors the real option row's box (padding/height/check-icon slot) so the
// list doesn't jump when data replaces it.
function OptionRowSkeleton({ index }: { readonly index: number }) {
  return (
    <div
      aria-hidden="true"
      className="flex w-full items-center justify-between gap-2 rounded-sm px-2 py-1.5"
    >
      <Skeleton
        className={cn(
          "h-4 rounded-sm",
          OPTION_ROW_WIDTHS[index % OPTION_ROW_WIDTHS.length],
        )}
      />
      <Skeleton className="size-4 shrink-0 rounded-sm" />
    </div>
  );
}

function OptionListSkeleton({
  rows,
  label,
}: {
  readonly rows: number;
  readonly label?: string;
}) {
  return (
    <>
      <span role="status" className="sr-only">
        {label}
      </span>
      {Array.from({ length: rows }, (_, index) => (
        <OptionRowSkeleton key={index} index={index} />
      ))}
    </>
  );
}

export interface ComboboxProps {
  readonly value: string;
  readonly onValueChange: (value: string) => void;
  readonly options: readonly ComboboxOption[];
  readonly placeholder?: string;
  readonly searchPlaceholder?: string;
  readonly emptyMessage?: string;
  readonly disabled?: boolean;
  readonly error?: boolean;
  readonly id?: string;
  readonly className?: string;
  readonly "aria-label"?: string;
  readonly "aria-describedby"?: string;
  readonly "aria-invalid"?: boolean | "true" | "false";
  /**
   * Providing `onSearchChange` switches the Combobox to async mode: the
   * search box is controlled by `searchValue` and `options` is trusted as
   * already-filtered server data instead of being filtered again locally.
   */
  readonly searchValue?: string;
  readonly onSearchChange?: (value: string) => void;
  /** First page in flight — replaces the option list with a spinner. */
  readonly isLoading?: boolean;
  /** A subsequent page in flight — renders a spinner below the loaded options. */
  readonly isFetchingMore?: boolean;
  readonly hasMore?: boolean;
  readonly onLoadMore?: () => void;
  /** Fallback label for a selected value that hasn't loaded into `options` yet. */
  readonly selectedOption?: ComboboxOption;
  readonly loadingLabel?: string;
}

/**
 * A search-filterable single-select. Built on Popover + a plain option list
 * rather than Radix Select — Select's Content owns focus via its own roving
 * tabindex group, which fights any input rendered inside it, so a search box
 * inside Select.Content can never reliably keep keyboard focus.
 */
export function Combobox({
  value,
  onValueChange,
  options,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  disabled,
  error,
  id,
  className,
  "aria-label": ariaLabel,
  "aria-describedby": ariaDescribedby,
  "aria-invalid": ariaInvalid,
  searchValue,
  onSearchChange,
  isLoading,
  isFetchingMore,
  hasMore,
  onLoadMore,
  selectedOption,
  loadingLabel,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const descriptionIdPrefix = React.useId();
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  // A state-backed ref (rather than a plain `useRef`) so the observer effect
  // below re-runs exactly when the sentinel mounts/unmounts — e.g. Radix
  // portalling the popover content in is a separate commit from the one
  // that flips `open`, so keying off `open` alone can race the DOM.
  const [sentinelNode, setSentinelNode] = React.useState<HTMLDivElement | null>(
    null,
  );

  const isAsync = onSearchChange !== undefined;

  const selected =
    options.find((option) => option.value === value) ?? selectedOption;
  const isInvalid =
    error === true || ariaInvalid === true || ariaInvalid === "true";

  const filteredOptions = React.useMemo(() => {
    if (isAsync) return options;
    const normalized = query.trim().toLowerCase();
    if (normalized === "") return options;
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(normalized) ||
        (option.description?.toLowerCase().includes(normalized) ?? false) ||
        (option.keywords?.toLowerCase().includes(normalized) ?? false),
    );
  }, [isAsync, options, query]);

  // Read through a ref so fetch-more cycles don't rebuild the observer.
  const loadMoreState = React.useRef({
    hasMore,
    isFetchingMore,
    isLoading,
    onLoadMore,
  });
  loadMoreState.current = { hasMore, isFetchingMore, isLoading, onLoadMore };

  React.useEffect(() => {
    if (sentinelNode === null) return;
    const root = scrollRef.current;
    if (root === null) return;

    let wasIntersecting = false;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry === undefined) return;
        if (!entry.isIntersecting) {
          wasIntersecting = false;
          return;
        }
        if (wasIntersecting) return;
        wasIntersecting = true;
        const state = loadMoreState.current;
        if (
          state.hasMore === true &&
          state.isFetchingMore !== true &&
          state.isLoading !== true
        ) {
          state.onLoadMore?.();
        }
      },
      { root, threshold: 0 },
    );
    observer.observe(sentinelNode);
    return () => observer.disconnect();
  }, [sentinelNode]);

  return (
    // `modal` gives the (portaled) option list its own scroll lock. Without
    // it, a Combobox inside a Dialog can't be wheel-scrolled -- the Dialog's
    // lock treats the portaled list as "outside" and swallows the wheel.
    <Popover
      modal
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) {
          if (isAsync) onSearchChange("");
          else setQuery("");
        }
      }}
    >
      <PopoverTrigger
        id={id}
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedby}
        aria-invalid={isInvalid ? true : undefined}
        disabled={disabled}
        className={cn(
          "group flex h-10 w-full items-center justify-between gap-2 rounded-md border bg-container-default-background px-3 py-2 text-body-m-r transition-colors focus:outline-none disabled:cursor-not-allowed disabled:opacity-50",
          isInvalid
            ? "border-fail-text text-fail-text focus:border-fail-text"
            : "border-container-default-border text-text focus:border-primary",
          className,
        )}
      >
        <span
          className={cn(
            "min-w-0 flex-1 truncate text-start",
            selected === undefined && "text-text-placeholder",
          )}
        >
          {selected?.label ?? placeholder}
          {selected?.description === undefined ? null : (
            <>
              {" "}
              {/* <bdi> keeps a Latin username from reordering inside RTL text. */}
              <bdi className="text-text-placeholder">
                ({selected.description})
              </bdi>
            </>
          )}
        </span>
        <ChevronDown
          className="size-4 shrink-0 text-secondary-icon opacity-50 transition-transform duration-140 ease-emphasized group-data-[state=open]:rotate-180"
          aria-hidden="true"
        />
      </PopoverTrigger>

      <PopoverContent
        align="start"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          searchInputRef.current?.focus();
        }}
        className="w-[var(--radix-popover-trigger-width)] min-w-[8rem] p-0"
      >
        <div className="flex items-center gap-2 border-b border-container-divider px-3 py-2">
          <Search
            className="size-4 shrink-0 text-text-placeholder"
            aria-hidden="true"
          />
          <input
            ref={searchInputRef}
            type="text"
            value={isAsync ? (searchValue ?? "") : query}
            onChange={(event) => {
              const nextValue = event.target.value;
              if (isAsync) onSearchChange(nextValue);
              else setQuery(nextValue);
            }}
            placeholder={searchPlaceholder}
            className="h-6 w-full border-0 bg-transparent p-0 text-body-m-r text-text placeholder:text-text-placeholder focus:outline-none"
          />
        </div>
        <div ref={scrollRef} className="max-h-60 overflow-y-auto p-1">
          {isLoading === true ? (
            <OptionListSkeleton
              rows={5}
              {...(loadingLabel === undefined ? {} : { label: loadingLabel })}
            />
          ) : filteredOptions.length === 0 ? (
            <p className="py-6 text-center text-body-s-r text-text-placeholder">
              {emptyMessage}
            </p>
          ) : (
            <>
              {filteredOptions.map((option, index) => {
                const isSelected = option.value === value;
                // The description is announced as the option's description,
                // not folded into its accessible name.
                const descriptionId =
                  option.description === undefined
                    ? undefined
                    : `${descriptionIdPrefix}-description-${index}`;
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    aria-labelledby={
                      descriptionId === undefined
                        ? undefined
                        : `${descriptionIdPrefix}-label-${index}`
                    }
                    aria-describedby={descriptionId}
                    onClick={() => {
                      onValueChange(option.value);
                      setOpen(false);
                      if (isAsync) onSearchChange("");
                      else setQuery("");
                    }}
                    className="flex w-full items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-start text-body-m-r text-text transition-colors hover:bg-container-hover"
                  >
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span id={`${descriptionIdPrefix}-label-${index}`} className="truncate">
                        {option.label}
                      </span>
                      {option.description === undefined ? null : (
                        <span
                          id={descriptionId}
                          className="truncate text-body-s-r text-text-placeholder"
                        >
                          <bdi>{option.description}</bdi>
                        </span>
                      )}
                    </span>
                    <Check
                      className={cn(
                        "size-4 shrink-0 text-primary",
                        isSelected ? "opacity-100" : "opacity-0",
                      )}
                      aria-hidden="true"
                    />
                  </button>
                );
              })}
              {onLoadMore === undefined ? null : (
                <div
                  ref={setSentinelNode}
                  aria-hidden="true"
                  className="h-px"
                />
              )}
              {isFetchingMore === true ? (
                <OptionListSkeleton
                  rows={2}
                  {...(loadingLabel === undefined
                    ? {}
                    : { label: loadingLabel })}
                />
              ) : null}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
